/**
 * The shape of a page, drawn before its numbers arrive.
 *
 * A cold window costs several seconds of GA4 round trips, and the page showed one line
 * of grey text for all of it. That reads as nothing happening — the layout arrives in
 * one jump at the end, and there is nothing on screen to suggest the wait is bounded.
 *
 * These stand in for the blocks that are coming, in the same places and at roughly the
 * same sizes, so the page is legible immediately and fills in rather than appearing. It
 * is not a progress bar: it makes no claim about how long is left, which is the one
 * thing this code genuinely does not know.
 *
 * Every skeleton is `aria-hidden` behind a single live region. A screen reader should
 * hear "loading" once, not read out a dozen empty boxes.
 */

export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div className="skeleton__tiles" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div className="skeleton__tile" key={index}>
          <span className="skeleton__line skeleton__line--label" />
          <span className="skeleton__line skeleton__line--figure" />
          <span className="skeleton__line skeleton__line--note" />
        </div>
      ))}
    </div>
  )
}

export function SkeletonChart() {
  return <div className="skeleton__chart" aria-hidden="true" />
}

export function SkeletonTable({ rows = 5, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="card skeleton__table" aria-hidden="true">
      <div className="skeleton__row skeleton__row--head">
        {Array.from({ length: columns }, (_, index) => (
          <span className="skeleton__line" key={index} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div className="skeleton__row" key={row}>
          {Array.from({ length: columns }, (_, index) => (
            <span className="skeleton__line" key={index} />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * The whole loading state for a page, with the one announcement that goes with it.
 *
 * `label` names what is being fetched rather than saying "loading", because the message
 * a reader hears on a slow connection should say which page they are waiting for.
 */
export function SkeletonPage({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <p className="visually-hidden" role="status" aria-live="polite">
        {label}
      </p>
      {children}
    </>
  )
}
